import { DOCUMENT } from '@angular/common';
import { APP_ID, CSP_NONCE, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ɵSharedStylesHost } from '@angular/platform-browser';

@Injectable()
export class CustomStylesHostService extends ɵSharedStylesHost {
  private readonly REM_BASE = 10;
  private transformStyleCache = new Map<string, string>();
  private hostUsage = new Map<Node, number>();

  constructor() {
    const doc = inject<Document>(DOCUMENT);
    const appId = inject(APP_ID);
    const nonce = inject(CSP_NONCE, { optional: true });
    const platformId = inject(PLATFORM_ID) ?? {};

    super(doc, appId, nonce, platformId);
    this['hosts'].clear();
  }

  public addStyles(styles: string[], urls: string[]): void {
    const transformedStyles = styles.map(style => {
      const cachedStyle = this.transformStyleCache.get(style);
      if (cachedStyle) {
        return cachedStyle;
      }

      const transformedStyle = this.remToPX(style);
      this.transformStyleCache.set(style, transformedStyle);
      return transformedStyle;
    });
    super.addStyles(transformedStyles, urls);
  }

  public removeStyles(styles: string[], urls: string[]): void {
    const transformedStyles = styles.map(style => this.transformStyleCache.get(style) ?? style);
    super.removeStyles(transformedStyles, urls);
  }

  /**
   * Angular registers/deregisters shadow roots as style hosts per component without
   * reference counting (angular/angular@cdda51a), so destroying any dynamically created
   * component inside a shared shadow root (e.g. the overlay container) strips ALL
   * component styles from it. Count host usage and only remove at zero.
   * Remove once Angular reference-counts style hosts itself.
   */
  public override addHost(hostNode: Node): void {
    this.hostUsage.set(hostNode, (this.hostUsage.get(hostNode) ?? 0) + 1);
    super.addHost(hostNode);
  }

  public override removeHost(hostNode: Node): void {
    const usage = (this.hostUsage.get(hostNode) ?? 0) - 1;
    if (usage > 0) {
      this.hostUsage.set(hostNode, usage);
      return;
    }

    this.hostUsage.delete(hostNode);
    super.removeHost(hostNode);
  }

  private remToPX(style: string): string {
    return style.replace(
      /(((\d+)|(\.\d+)|(\d+\.\d+))rem)/g,
      match => `${parseFloat(match) * this.REM_BASE}px`,
    );
  }
}
